import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import DocumentSelector from './DocumentSelector';
import { getStageData, saveStageData, completeStage } from '../../../api/claims';

/**
 * Generic modal for stages that primarily collect notes/documents
 * Used for: Calculation, Cost/Price/Profit, Quote/Negotiation, Settlement/Invoicing, Closed
 */
const SimpleStageModal = ({ 
  open, 
  onOpenChange, 
  claimId, 
  stageName, 
  stageTitle, 
  stageDescription,
  notesLabel,
  notesPlaceholder,
  showDocuments = true,
  onComplete 
}) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch stage data
  const { data: stageDataResponse, isLoading } = useQuery({
    queryKey: ['stageData', claimId, stageName],
    queryFn: () => getStageData(claimId, stageName),
    enabled: open && !!claimId,
    onSuccess: (data) => {
      if (data?.data?.stage_data?.notes) {
        setNotes(data.data.stage_data.notes);
      }
    }
  });

  const stageData = stageDataResponse?.data || {};

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => saveStageData(claimId, stageName, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId, stageName]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      setHasChanges(false);
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: () => completeStage(claimId, stageName),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      if (onComplete) onComplete();
      onOpenChange(false);
    }
  });

  const handleNotesChange = (value) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate({ notes });
  };

  const handleComplete = () => {
    if (hasChanges) {
      saveMutation.mutate({ notes }, {
        onSuccess: () => {
          completeMutation.mutate();
        }
      });
    } else {
      completeMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{stageTitle}</span>
            {stageData.is_completed && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </DialogTitle>
          <DialogDescription>{stageDescription}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">{notesLabel}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder={notesPlaceholder}
                rows={6}
                disabled={stageData.is_completed}
              />
            </div>

            {showDocuments && (
              <div className="space-y-2">
                <Label>Supporting Documents</Label>
                <DocumentSelector
                  claimId={claimId}
                  stageName={stageName}
                  onDocumentAdded={() => setHasChanges(true)}
                />
              </div>
            )}

            {stageData.is_completed && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  This stage was completed on {new Date(stageData.completed_at).toLocaleDateString()}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!stageData.is_completed && (
            <>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saveMutation.isPending || !hasChanges}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Draft'
                )}
              </Button>
              <Button
                onClick={handleComplete}
                disabled={completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleStageModal;